import React from 'react';
import { Input, Select, Switch, Table } from 'antd';
import { TagsOutlined } from '@ant-design/icons';
import { IActivity, IMotif } from '@leedpulse-monorepo/utils';

// Mirrors backend/src/api/v1/ai/ai.controller.ts CUSTOM_INSTRUCTION_MAX_LENGTH.
const CUSTOM_INSTRUCTION_MAX_LENGTH = 1000;

interface ActivityConfig {
  activity_id: string;
  enabled: boolean;
}

interface MotifConfig {
  motif_id: string;
  mode: 'automatic' | 'custom';
  custom_instruction?: string;
}

interface AIReceptionistCatalogEditorProps {
  availableActivities: IActivity[];
  availableMotifs: IMotif[];
  activities: ActivityConfig[];
  motifs: MotifConfig[];
  onActivitiesChange: (activities: ActivityConfig[]) => void;
  onMotifsChange: (motifs: MotifConfig[]) => void;
  disabled?: boolean;
}

const AIReceptionistCatalogEditor: React.FC<AIReceptionistCatalogEditorProps> = ({
  availableActivities,
  availableMotifs,
  activities,
  motifs,
  onActivitiesChange,
  onMotifsChange,
  disabled = false,
}) => {
  const handleActivityToggle = (activityId: string, enabled: boolean) => {
    onActivitiesChange(
      activities.some((activity) => activity.activity_id === activityId)
        ? activities.map((activity) =>
          activity.activity_id === activityId ? { ...activity, enabled } : activity
        )
        : [...activities, { activity_id: activityId, enabled }]
    );
  };

  const handleMotifModeChange = (motifId: string, mode: MotifConfig['mode']) => {
    onMotifsChange(
      motifs.some((motif) => motif.motif_id === motifId)
        ? motifs.map((motif) => (motif.motif_id === motifId ? { ...motif, mode } : motif))
        : [...motifs, { motif_id: motifId, mode }]
    );
  };

  const handleMotifInstructionChange = (motifId: string, customInstruction: string) => {
    onMotifsChange(
      motifs.some((motif) => motif.motif_id === motifId)
        ? motifs.map((motif) =>
          motif.motif_id === motifId ? { ...motif, custom_instruction: customInstruction } : motif
        )
        : [...motifs, { motif_id: motifId, mode: 'custom', custom_instruction: customInstruction }]
    );
  };

  const activityColumns = [
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: 'Activée pour cette IA',
      key: 'enabled',
      width: 160,
      render: (_: unknown, record: IActivity) => (
        <Switch
          disabled={disabled}
          checked={activities.find((activity) => activity.activity_id === record.id)?.enabled ?? false}
          onChange={(checked) => handleActivityToggle(record.id, checked)}
        />
      ),
    },
  ];

  const motifColumns = [
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: 'Mode',
      key: 'mode',
      width: 160,
      render: (_: unknown, record: IMotif) => (
        <Select
          disabled={disabled}
          style={{ width: 140 }}
          value={motifs.find((motif) => motif.motif_id === record.id)?.mode ?? 'automatic'}
          onChange={(mode) => handleMotifModeChange(record.id, mode)}
        >
          <Select.Option value="automatic">Automatique</Select.Option>
          <Select.Option value="custom">Personnalisé</Select.Option>
        </Select>
      ),
    },
    {
      title: 'Instruction personnalisée',
      key: 'custom_instruction',
      render: (_: unknown, record: IMotif) => {
        const config = motifs.find((motif) => motif.motif_id === record.id);
        if ((config?.mode ?? 'automatic') !== 'custom') return null;
        const isMissing = !config?.custom_instruction?.trim();
        return (
          <>
            <Input.TextArea
              disabled={disabled}
              rows={2}
              maxLength={CUSTOM_INSTRUCTION_MAX_LENGTH}
              showCount
              status={isMissing ? 'error' : ''}
              placeholder="Instruction utilisée à la place du flux par défaut du motif."
              value={config?.custom_instruction ?? ''}
              onChange={(e) => handleMotifInstructionChange(record.id, e.target.value)}
            />
            {isMissing && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                Instruction requise en mode personnalisé, sinon le flux automatique du motif s&apos;applique.
              </div>
            )}
          </>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 16 }}>
          <TagsOutlined style={{ marginRight: 6 }} /> Activités
        </h2>
        <Table
          dataSource={availableActivities}
          columns={activityColumns}
          rowKey="id"
          pagination={false}
          size="middle"
          locale={{ emptyText: 'Aucune activité configurée pour le domaine de cette IA. Configurez le catalogue depuis la fiche du domaine.' }}
        />
      </div>

      <div>
        <h2 style={{ marginBottom: 16 }}>
          <TagsOutlined style={{ marginRight: 6 }} /> Motifs
        </h2>
        <Table
          dataSource={availableMotifs}
          columns={motifColumns}
          rowKey="id"
          pagination={false}
          size="middle"
          locale={{ emptyText: 'Aucun motif configuré pour le domaine de cette IA.' }}
        />
      </div>
    </>
  );
};

export default AIReceptionistCatalogEditor;
